import { addDays, addHours, differenceInMinutes, endOfDay, format, startOfDay } from 'date-fns';
import * as React from 'react';
import type { IconType } from 'react-icons';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import type { EventView } from '@ndvr/customer-api';

import { layoutCalendarSidebarTimedEvents } from '@/components/calendar-sidebar-layout';
import { convertBackendTimeToBrowser, isFullDayEvent } from '@/utils';
import { cn } from '@/utils/cn';
import { useCalendarViews, useHasConnectedCalendar } from '@/views/calendar/calendar-queries';
import { ConnectCalendarCard } from '@/views/calendar/widgets/ConnectCalendarCard';
import { EventDetailsBubble } from '@/views/household/HouseholdEvents/EventDetailsBubble';

const CALENDAR_HOUR_HEIGHT = 79;
const CALENDAR_DEFAULT_START_HOUR = 8;
const CALENDAR_DEFAULT_END_HOUR = 18;
const CALENDAR_COMPACT_EVENT_HEIGHT = 39;
const CALENDAR_MIN_EVENT_HEIGHT = 28;
const CALENDAR_EVENT_COLUMN_GAP = 4;
const CALENDAR_PRELOAD_DAYS = 3;
const CALENDAR_PRELOAD_RANGE_DAYS = CALENDAR_PRELOAD_DAYS * 2 + 1;
const CALENDAR_LOADING_EVENT_SKELETONS = [
    {
        height: 58,
        key: 'loading-event-1',
        left: '0%',
        top: CALENDAR_HOUR_HEIGHT * 0.75,
        width: '78%',
    },
    {
        height: 44,
        key: 'loading-event-2',
        left: '14%',
        top: CALENDAR_HOUR_HEIGHT * 2.35,
        width: '60%',
    },
    {
        height: 74,
        key: 'loading-event-3',
        left: '0%',
        top: CALENDAR_HOUR_HEIGHT * 4.65,
        width: '84%',
    },
];

type CalendarEventTone = 'filled' | 'outline' | 'purple';

const CALENDAR_SIDEBAR_EVENT_TONE_CLASSES: Record<CalendarEventTone, string> = {
    filled: 'border border-[#C2D4D7] bg-white hover:bg-[#F4FAFB]',
    outline: 'border border-[#CEC6CA] bg-white hover:bg-[#E6E6E6]',
    purple: 'bg-seafoam-100 hover:bg-[#C2D4D7]',
};

interface CalendarSidebarEvent {
    event: EventView;
    height: number;
    time: string;
    eventGUID: string;
    title: string;
    top: number;
    tone: CalendarEventTone;
    startsAt: number;
    endsAt: number;
    columnCount: number;
    columnIndex: number;
    columnSpan: number;
}

const CalendarSidebarIconButton: React.FC<{
    ariaLabel: string;
    icon: IconType;
    onClick?: () => void;
}> = ({ ariaLabel, icon: Icon, onClick }) => {
    return (
        <button
            type="button"
            aria-label={ariaLabel}
            onClick={onClick}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[#897E7B] transition-colors hover:bg-[#F8F7F5] hover:text-[#5A4F4E] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
            <Icon className="h-4 w-4" />
        </button>
    );
};

const CalendarSidebarStatusPanel: React.FC<{
    description: string;
    title: string;
}> = ({ description, title }) => {
    return (
        <div className="flex min-h-[240px] items-center justify-center px-4">
            <div className="max-w-[280px] text-center">
                <h3 className="font-halyard text-[16px] leading-none font-medium tracking-[-0.02em] text-[#5A4F4E]">
                    {title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#897E7B]">{description}</p>
            </div>
        </div>
    );
};

const CalendarSidebarLoadingState: React.FC<{
    scheduleHeight: number;
    timeLabels: string[];
}> = ({ scheduleHeight, timeLabels }) => {
    return (
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2" aria-hidden="true">
            <div className="relative w-12">
                {timeLabels.map((label, index) => (
                    <div
                        key={label}
                        className="font-halyard absolute left-0 text-[12px] leading-[1.2] font-normal tracking-[-0.02em] text-[#897E7B]"
                        style={{ top: index * CALENDAR_HOUR_HEIGHT - 8 }}
                    >
                        {label}
                    </div>
                ))}
            </div>

            <div className="relative min-w-0" style={{ height: scheduleHeight + 24 }}>
                {timeLabels.map((label, index) => (
                    <div
                        key={`${label}-line`}
                        className="absolute right-0 left-0 border-t border-[#ECE7E3]"
                        style={{ top: index * CALENDAR_HOUR_HEIGHT }}
                    />
                ))}

                {CALENDAR_LOADING_EVENT_SKELETONS.map(({ height, key, left, top, width }) => (
                    <div
                        key={key}
                        className="absolute animate-pulse rounded-[8px] bg-[#F3EFEB] px-2 py-1.5"
                        style={{ height, left, top, width }}
                    >
                        <div className="space-y-2">
                            <div className="h-3 w-3/4 rounded bg-white/70" />
                            <div className="h-3 w-1/2 rounded bg-white/55" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

function getCalendarSidebarEventTone(event: EventView): CalendarEventTone {
    if (event.dateSpan || event.isDateSpan) {
        return 'outline';
    }
    return event.clientHouseholdGUID ? 'purple' : 'filled';
}

function getCalendarSidebarEventTitle(event: EventView): string {
    return event.title?.trim() || 'Meeting';
}

function getCalendarSidebarEventToneClasses(tone: CalendarEventTone): string {
    return CALENDAR_SIDEBAR_EVENT_TONE_CLASSES[tone];
}

function getCalendarSidebarEvents(
    events: EventView[] | undefined,
    selectedDate: Date,
): {
    allDayEvents: EventView[];
    scheduleEndHour: number;
    scheduleStartHour: number;
    scheduledEvents: CalendarSidebarEvent[];
} {
    if (!events?.length) {
        return {
            allDayEvents: [],
            scheduleEndHour: CALENDAR_DEFAULT_END_HOUR,
            scheduleStartHour: CALENDAR_DEFAULT_START_HOUR,
            scheduledEvents: [],
        };
    }

    const dayStart = startOfDay(selectedDate);
    const dayEnd = endOfDay(selectedDate);

    const allDayEvents: EventView[] = [];
    const scheduledEvents: CalendarSidebarEvent[] = [];
    const timedEvents: Array<{ end: Date; event: EventView; start: Date }> = [];

    events.forEach((event) => {
        const start = convertBackendTimeToBrowser(event.startTime, event.startTimeZone);
        const end = convertBackendTimeToBrowser(event.endTime, event.endTimeZone);

        if (start > dayEnd || end < dayStart) {
            return;
        }

        if (event.dateSpan || event.isDateSpan || isFullDayEvent(start, end)) {
            allDayEvents.push(event);
            return;
        }

        const clippedStart = start < dayStart ? dayStart : start;
        const clippedEnd = end > dayEnd ? dayEnd : end;

        if (clippedEnd <= clippedStart) {
            return;
        }

        timedEvents.push({ event, start: clippedStart, end: clippedEnd });
    });

    const scheduleStartHour = timedEvents.length
        ? Math.max(
              0,
              Math.min(CALENDAR_DEFAULT_START_HOUR, Math.min(...timedEvents.map(({ start }) => start.getHours()))),
          )
        : CALENDAR_DEFAULT_START_HOUR;
    const scheduleEndHour = timedEvents.length
        ? Math.min(
              24,
              Math.max(
                  CALENDAR_DEFAULT_END_HOUR,
                  Math.max(
                      ...timedEvents.map(({ end }) => {
                          const hour = end.getHours();
                          return end.getMinutes() > 0 || end.getSeconds() > 0 || end.getMilliseconds() > 0
                              ? hour + 1
                              : hour;
                      }),
                  ),
              ),
          )
        : CALENDAR_DEFAULT_END_HOUR;
    const scheduleStart = addHours(dayStart, scheduleStartHour);
    const scheduleEnd = addHours(dayStart, scheduleEndHour);

    timedEvents.forEach(({ end, event, start }) => {
        const clippedStart = start < scheduleStart ? scheduleStart : start;
        const clippedEnd = end > scheduleEnd ? scheduleEnd : end;

        if (clippedEnd <= clippedStart) {
            return;
        }

        const top = (differenceInMinutes(clippedStart, scheduleStart) / 60) * CALENDAR_HOUR_HEIGHT;
        const height = Math.max(
            (differenceInMinutes(clippedEnd, clippedStart) / 60) * CALENDAR_HOUR_HEIGHT,
            CALENDAR_MIN_EVENT_HEIGHT,
        );

        scheduledEvents.push({
            event,
            eventGUID: event.eventGUID,
            title: getCalendarSidebarEventTitle(event),
            time: `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
            top,
            height,
            tone: getCalendarSidebarEventTone(event),
            startsAt: start.getTime(),
            endsAt: end.getTime(),
            columnCount: 1,
            columnIndex: 0,
            columnSpan: 1,
        });
    });

    scheduledEvents.sort((a, b) => a.startsAt - b.startsAt || b.endsAt - a.endsAt);
    allDayEvents.sort((a, b) => getCalendarSidebarEventTitle(a).localeCompare(getCalendarSidebarEventTitle(b)));

    return {
        allDayEvents,
        scheduleEndHour,
        scheduleStartHour,
        scheduledEvents: layoutCalendarSidebarTimedEvents(scheduledEvents),
    };
}

function getCalendarSidebarRangeStart(selectedDate: Date): Date {
    return startOfDay(addDays(selectedDate, -CALENDAR_PRELOAD_DAYS));
}

function isDateOutsideCalendarSidebarRange(selectedDate: Date, rangeStartDate: Date): boolean {
    return selectedDate < rangeStartDate || selectedDate >= addDays(rangeStartDate, CALENDAR_PRELOAD_RANGE_DAYS);
}

export const CalendarSidebarPanel: React.FC = () => {
    const [selectedDate, setSelectedDate] = React.useState(() => startOfDay(new Date()));
    const [rangeStartDate, setRangeStartDate] = React.useState(() =>
        getCalendarSidebarRangeStart(startOfDay(new Date())),
    );
    const [selectedEventGUID, setSelectedEventGUID] = React.useState<string | null>(null);
    const hasConnectedCalendar = useHasConnectedCalendar();
    const fromDate = rangeStartDate;
    const toDate = React.useMemo(() => addDays(rangeStartDate, CALENDAR_PRELOAD_RANGE_DAYS), [rangeStartDate]);
    const calendarViews = useCalendarViews({
        fromDate: hasConnectedCalendar.data ? fromDate : undefined,
        toDate: hasConnectedCalendar.data ? toDate : undefined,
    });
    const { allDayEvents, scheduleEndHour, scheduleStartHour, scheduledEvents } = React.useMemo(
        () => getCalendarSidebarEvents(calendarViews.data?.calendarEvents, selectedDate),
        [calendarViews.data?.calendarEvents, selectedDate],
    );
    const isLoading = hasConnectedCalendar.isPending || (hasConnectedCalendar.data === true && calendarViews.isPending);
    const hasAllDayEvents = allDayEvents.length > 0;
    const calendarTimeLabels = React.useMemo(
        () =>
            Array.from({ length: scheduleEndHour - scheduleStartHour + 1 }, (_, index) =>
                format(addHours(startOfDay(selectedDate), scheduleStartHour + index), 'h a'),
            ),
        [scheduleEndHour, scheduleStartHour, selectedDate],
    );
    const calendarScheduleHeight = CALENDAR_HOUR_HEIGHT * (scheduleEndHour - scheduleStartHour);

    React.useEffect(() => {
        setSelectedEventGUID(null);
    }, [selectedDate]);

    function navigateSelectedDate(dayOffset: number): void {
        const nextSelectedDate = startOfDay(addDays(selectedDate, dayOffset));

        setSelectedDate(nextSelectedDate);
        setRangeStartDate((currentRangeStartDate) =>
            isDateOutsideCalendarSidebarRange(nextSelectedDate, currentRangeStartDate)
                ? getCalendarSidebarRangeStart(nextSelectedDate)
                : currentRangeStartDate,
        );
    }

    return (
        <div className="h-full overflow-y-auto px-6 pb-8 select-none">
            <div className="flex w-full min-w-0 flex-col gap-6">
                <div className="flex items-center justify-between pt-2">
                    <div className="flex flex-col gap-1">
                        <div className="text-[10px] leading-none font-medium tracking-[0.08em] text-[#897E7B] uppercase">
                            {format(selectedDate, 'EEEE')}
                        </div>
                        <h2 className="font-halyard text-[18px] leading-none font-medium tracking-[-0.02em] text-[#5A4F4E]">
                            {format(selectedDate, 'MMM do, yyyy')}
                        </h2>
                    </div>
                    <div className="flex items-center gap-0.5">
                        <CalendarSidebarIconButton
                            ariaLabel="Previous day"
                            icon={LuChevronLeft}
                            onClick={() => navigateSelectedDate(-1)}
                        />
                        <CalendarSidebarIconButton
                            ariaLabel="Next day"
                            icon={LuChevronRight}
                            onClick={() => navigateSelectedDate(1)}
                        />
                    </div>
                </div>

                {hasConnectedCalendar.data === false ? (
                    <ConnectCalendarCard />
                ) : isLoading ? (
                    <CalendarSidebarLoadingState
                        timeLabels={calendarTimeLabels}
                        scheduleHeight={calendarScheduleHeight}
                    />
                ) : calendarViews.isError ? (
                    <CalendarSidebarStatusPanel
                        title="Calendar unavailable"
                        description="We couldn't load your events for this day. Try again in a moment."
                    />
                ) : (
                    <>
                        {hasAllDayEvents ? (
                            <div className="space-y-2">
                                <div className="text-xs font-medium tracking-[0.04em] text-[#897E7B] uppercase">
                                    All day
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {allDayEvents.map((event) => (
                                        <EventDetailsBubble
                                            key={event.eventGUID}
                                            event={event}
                                            open={selectedEventGUID === event.eventGUID}
                                            onOpenChange={(open) => setSelectedEventGUID(open ? event.eventGUID : null)}
                                            trigger={
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'cursor-pointer rounded-full px-3 py-1.5 text-left text-xs text-[#5A4F4E] transition-colors select-none focus-visible:ring-2 focus-visible:ring-[#C9BBB4] focus-visible:ring-offset-2 focus-visible:outline-none',
                                                        getCalendarSidebarEventToneClasses(
                                                            getCalendarSidebarEventTone(event),
                                                        ),
                                                    )}
                                                >
                                                    {getCalendarSidebarEventTitle(event)}
                                                </button>
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-2">
                            <div className="relative w-10">
                                {calendarTimeLabels.map((label, index) => (
                                    <div
                                        key={label}
                                        className="font-halyard absolute left-0 text-[12px] leading-[1.2] font-normal tracking-[-0.02em] text-[#897E7B]"
                                        style={{ top: index * CALENDAR_HOUR_HEIGHT - 8 }}
                                    >
                                        {label}
                                    </div>
                                ))}
                            </div>

                            <div
                                className="relative min-w-0"
                                aria-label="Calendar day view"
                                style={{ height: calendarScheduleHeight + 24 }}
                            >
                                {calendarTimeLabels.map((label, index) => (
                                    <div
                                        key={`${label}-line`}
                                        className="absolute right-0 left-0 border-t border-[#ECE7E3]"
                                        style={{ top: index * CALENDAR_HOUR_HEIGHT }}
                                    />
                                ))}

                                {scheduledEvents.map(
                                    ({
                                        columnCount,
                                        columnIndex,
                                        columnSpan,
                                        event,
                                        eventGUID,
                                        height,
                                        time,
                                        title,
                                        top,
                                        tone,
                                    }) => {
                                        const showTime = height >= CALENDAR_COMPACT_EVENT_HEIGHT;
                                        const leftPercent = (columnIndex / columnCount) * 100;
                                        const widthPercent = (columnSpan / columnCount) * 100;
                                        const horizontalInset = columnCount > 1 ? CALENDAR_EVENT_COLUMN_GAP / 2 : 0;
                                        const widthReduction = columnCount > 1 ? CALENDAR_EVENT_COLUMN_GAP : 0;

                                        return (
                                            <EventDetailsBubble
                                                key={eventGUID}
                                                event={event}
                                                open={selectedEventGUID === eventGUID}
                                                onOpenChange={(open) => setSelectedEventGUID(open ? eventGUID : null)}
                                                trigger={
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            'absolute flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-[8px] px-2 py-1 text-left text-[#5A4F4E] transition-colors select-none focus-visible:ring-2 focus-visible:ring-[#C9BBB4] focus-visible:ring-offset-2 focus-visible:outline-none',
                                                            getCalendarSidebarEventToneClasses(tone),
                                                        )}
                                                        style={{
                                                            top,
                                                            height,
                                                            left: `calc(${leftPercent}% + ${horizontalInset}px)`,
                                                            zIndex: columnIndex + 1,
                                                            width:
                                                                widthReduction > 0
                                                                    ? `max(0px, calc(${widthPercent}% - ${widthReduction}px))`
                                                                    : `${widthPercent}%`,
                                                        }}
                                                    >
                                                        <div className="font-halyard truncate text-[12px] leading-[1.2] font-medium tracking-[-0.02em]">
                                                            {title}
                                                        </div>
                                                        {showTime ? (
                                                            <div className="font-halyard text-cocoa-500 bg-transparent text-[12px] leading-[1.2] font-normal tracking-[-0.02em]">
                                                                {time}
                                                            </div>
                                                        ) : null}
                                                    </button>
                                                }
                                            />
                                        );
                                    },
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
